import {
    PriceCalculationParameterInfo,
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type BoundRangeValue,
    type ParameterValueKind,
    type ServiceDefinition,
} from "../api";
import { PriceCalculationUnitValue } from "../configuration/PriceCalculationUnitValue";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a range value with minimum, maximum, step, and label.
 * Used for numeric parameters that have a range of possible values.
 */
export class ResolvedRangeValue extends ResolvedParameterValue<number, BoundRangeValue> {
    readonly min: number;
    readonly max: number;
    readonly step: number;
    readonly label: string;
    readonly unit?: PriceCalculationUnit;
    /**
     * Creates a new range value.
     *
     * @param value The range data containing min, max, step, and label bound to a parameter
     */
    constructor(value: Omit<BoundRangeValue, "kind">) {
        super({
            ...value,
            kind: "range",
        });
        this.min = value.min ?? 0;
        this.max = value.max ?? 100;
        this.step = value.step ?? 1;
        this.label = value.label ?? "";
        this.unit = value.unit ?? undefined;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the minimum value of the range.
     *
     * @returns The minimum value. Defaults to 0.
     */
    getMin(): number {
        return this.min;
    }

    /**
     * Gets the maximum value of the range.
     *
     * @returns The maximum value. Defaults to 100.
     */
    getMax(): number {
        return this.max;
    }

    /**
     * Gets the step value between numbers in the range.
     *
     * @returns The step value. Defaults to 1.
     */
    getStep(): number {
        return this.step;
    }

    /**
     * Gets the display label for the range.
     *
     * @returns The range's label.
     */
    getLabel(): string {
        return this.label;
    }

    /**
     * Gets the display unit for the range.
     *
     * @returns The range's unit.
     */
    getUnit(): PriceCalculationUnit | undefined {
        return this.unit;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        if (this.unit === undefined) {
            return [];
        }
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit,
                combineWithParameters: this.combineWithParameters,
            },
        ];
    }

    /**
     * Gets the value for the calculation parameter.
     *
     * @param value The value of the parameter
     * @returns The value for the calculation parameter
     */
    getCalculationParameterValues(value: number | undefined): Record<string, PriceCalculationValueWithUnit> {
        if (value === undefined || this.unit === undefined || value < this.min || value > this.max) {
            return {};
        }
        return { [this.parameterId.toString()]: { value: value, unit: this.unit } };
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(values: number | undefined): string | undefined {
        if (values === undefined) {
            return undefined;
        }
        const unitText: string = this.unit ? " " + PriceCalculationUnitValue.create(this.unit).getText() : "";
        return values.toString() + unitText;
    }

    /**
     * Creates a customer-safe version of this value.
     * This value type has no pricing data to strip.
     *
     * @param _requestPricing - The pricing mode (unused - no pricing data in this type)
     * @returns This instance (no changes needed for any pricing mode)
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): this {
        return this;
    }

    /**
     * Creates a new range value with valid step and label.
     *
     * @param value The range data containing min, max, step, and label bound to a parameter
     * @returns A new range value or throws an error if the values are invalid
     */
    static create(value: Omit<BoundRangeValue, "kind">): ResolvedRangeValue | never {
        const { min, max, step } = value;
        if (min && max && min >= max) {
            throw new Error("RangeValue: Property 'min' must be smaller than property 'max'.");
        }

        if (step && step <= 0) {
            throw new Error("RangeValue: Property 'step' must be greater than 0.");
        }

        if (value.value !== undefined && ((min && value.value < min) || (max && value.value > max))) {
            throw new Error("RangeValue: default value must be between 'min' and 'max'.");
        }

        return new ResolvedRangeValue(value);
    }
}
